/**
 * SearchField component for OLMap.
 * Used for triggering searches to a configured onSearch effect which can be
 * integrated with external services.
 */
import React, { useState, useRef, useEffect } from "react";
import { injectIntl } from "react-intl";
import useOnClickOutside from "use-onclickoutside";
import { useDebounce } from "use-lodash-debounce";
import {
  Wrapper,
  SearchWrapper,
  SearchInput,
  SearchIcon,
  ClearIcon,
  SuggestionsWrapper,
  SuggestionsList,
  Suggestion,
  ClearButton,
} from "./styled";

const SearchField = (props) => {
  const {
    id,
    extra = {},
    dispatchEvent,
    searchResult = [],
    builder,
    addFeature,
    hasTriggeredSearch,
    intl,
    map,
    effects,
  } = props;
  const { events = {} } = extra;
  const { onSearch, onSelect } = events;
  const [searchValue, setSearchValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(searchResult.length);
  const [selected, setSelected] = useState("");
  const ref = useRef(null);
  const inputRef = useRef(null);
  const suggestions = useRef([]);
  const didMount = useRef(false);
  const hasTriggeredOnSelect = useRef(false);
  const debouncedValue = useDebounce(searchValue, 500);

  const onSelectResult = effects[`${id}_onSelect`];

  useEffect(() => {
    if (didMount.current) {
      if (onSearch && searchValue) {
        dispatchEvent("onSearch", { searchValue });
        hasTriggeredSearch.current = true;
      }
    } else {
      didMount.current = true;
    }
  }, [debouncedValue]);

  useEffect(() => {
    if (!onSelectResult) {
      return;
    }

    const { long, lat } = onSelectResult;
    const coordinates = { long: parseFloat(long), lat: parseFloat(lat) };
    _addFeature({ ...selected, ...(onSelectResult || {}) }, coordinates);
  }, [onSelectResult]);

  useOnClickOutside(ref, () => setShowSuggestions(false));

  const _addFeature = (selectedValue = {}, coordinates) => {
    const long = parseFloat(coordinates?.long);
    const lat = parseFloat(coordinates?.lat);
    const coordinatesArray = [long, lat];

    const addressWithAddedCoordinates = {
      ...selectedValue,
      lat,
      long,
    };

    addFeature(addressWithAddedCoordinates);
    setShowSuggestions(false);
    setSelected(null);
    setSearchValue(null);

    // If we have coordinates, center the map to them
    if (long && lat) {
      map.getView().setCenter(coordinatesArray);
    }
  };

  /**
   * Handles what happens when the user clicks on a search suggestion. Adds the
   * feature related to chosen suggestion to the map and resets component state.
   *
   * @param {Object} value - The selected search suggestion.
   */
  const handleSelectSuggestion = async (selectedValue) => {
    if (!selectedValue) return;

    if (onSelect) {
      dispatchEvent("onSelect", { ...selectedValue });
      hasTriggeredOnSelect.current = true;
      setSelected(selectedValue);
    } else {
      const { lat, long } = selectedValue;
      _addFeature(selectedValue, { lat, long });
    }
  };

  /**
   * Overrides default keydown events for up and down key when the search input has focus.
   * Up and down keys switches focus from input to suggestions list.
   *
   * @param {KeyboardEvent} e
   */
  const onKeyDownInput = (e) => {
    if (e.code === "ArrowUp") {
      e.preventDefault();
      suggestions.current[suggestions.current?.length - 1]?.focus();
    }

    if (e.code === "ArrowDown") {
      e.preventDefault();
      suggestions.current[0]?.focus();
    }
  };

  /**
   * Overrides default keydown events for up, down and enter key when suggestions list has focus.
   * Up and down keys switches focus from suggestions list to search input. Enter key selects
   * the highlighted suggestion.
   *
   * @param {KeyboardEvent} e
   * @param {Object} result - The selected search suggestion.
   * @param {number} index - Index of selected suggestion in search results array.
   */
  const onKeyDownSuggestion = (e, result, index) => {
    if (e.code === "Enter") {
      e.preventDefault();
      handleSelectSuggestion(result);
    }

    if (e.code === "ArrowUp") {
      e.preventDefault();
      if (index === 0) {
        focusInput();
      } else {
        suggestions.current[index - 1]?.focus();
      }
    }

    if (e.code === "ArrowDown") {
      e.preventDefault();
      if (index === suggestions.current?.length - 1) {
        focusInput();
      } else {
        suggestions.current[index + 1]?.focus();
      }
    }
  };

  const focusInput = () => {
    inputRef.current?.focus();
  };

  const clearSearchInput = () => {
    setSearchValue("");
    setSelected("");
    focusInput();
    setShowSuggestions(false);
  };

  const SuggestionItem = ({ result, index }) => {
    if (result) {
      return (
        <Suggestion
          key={`suggestion_${index}`}
          tabIndex="0"
          ref={(el) => (suggestions.current[index] = el)}
          onClick={() => handleSelectSuggestion(result)}
          onKeyDown={(e) => onKeyDownSuggestion(e, result, index)}
        >
          <i
            className="fa-light fa-location-dot"
            style={{ marginRight: "10px" }}
          ></i>
          {result?.name}
        </Suggestion>
      );
    } else {
      return (
        <Suggestion className="disabled">
          {intl.formatMessage({ id: "OLMap.noResult" })}
        </Suggestion>
      );
    }
  };

  const renderIcon = () => {
    if (selected || searchValue) {
      return (
        <ClearButton>
          <ClearIcon
            role="button"
            aria-label="Clear search input"
            className="fa fa-xmark fa-xl"
            disabled={builder}
            onClick={() => clearSearchInput()}
          />
        </ClearButton>
      );
    } else {
      return (
        <SearchIcon
          className="fa fa-magnifying-glass fa-xl"
          disabled={builder}
        />
      );
    }
  };

  return (
    <Wrapper ref={ref}>
      <SearchWrapper
        showSuggestions={searchValue && showSuggestions}
        disabled={builder}
      >
        <SearchInput
          placeholder={intl.formatMessage({ id: "OLMap.searchPlaceholder" })}
          value={searchValue ?? selected}
          disabled={builder}
          ref={inputRef}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={onKeyDownInput}
          onFocus={() => setShowSuggestions(true)}
        ></SearchInput>
        {renderIcon()}
      </SearchWrapper>
      {searchValue && showSuggestions && (
        <SuggestionsWrapper>
          <SuggestionsList tabIndex="0">
            {!!searchResult.length &&
              Array.isArray(searchResult) &&
              searchResult.map((result, index) => (
                <SuggestionItem
                  key={`suggestion_${index}`}
                  result={result}
                  index={index}
                />
              ))}
            {searchResult.length === 0 && (
              <Suggestion className="disabled">
                {intl.formatMessage({ id: "OLMap.noResult" })}
              </Suggestion>
            )}
          </SuggestionsList>
        </SuggestionsWrapper>
      )}
    </Wrapper>
  );
};

export default injectIntl(SearchField);
